<template>
  <b-navbar class="app-header" toggleable="lg" type="dark" sticky>  
    <b-badge class="app-header-dev-badge" v-if="isEnvDevelopment && !isMobile" variant="danger">
      DEV
    </b-badge>
    <b-badge class="app-header-dev-badge" v-if="isEnvTest && !isMobile" variant="warning">
      TEST
    </b-badge>

    <b-navbar-brand href="/" v-if="!isMobile && !isOffline">
      <img v-if="logoUrl"
        class="app-header-logo float-left"
        :src="logoUrl"
      />
    </b-navbar-brand>

    <img v-if="logoUrl && (isMobile || isOffline)"
      class="app-header-logo float-left"
      :src="logoUrl"
    />

    <div v-if="currentPlantName" class="app-header-current-plant-name">
      <b-badge variant="primary">
        <span v-if="isPlantUnderConstruction" class="app-header-current-plant-construcion-icon"><app-icon icon="construction" /></span>
        {{ currentPlantName }}
      </b-badge>
    </div>    

    <ul v-if="isAdmin && isMobile && !isOffline" class="navbar-nav app-header-notification-mobile">
      <app-notification-bell />
    </ul>

    <b-navbar-toggle target="nav-collapse" v-show="!isOffline">
      <template #default="{ expanded }">
        <app-icon class="text-primary" icon="list" />
      </template>
    </b-navbar-toggle>
    
    <div v-if="isOffline" class="mar-left">
      <b-icon-cloud-slash  />
    </div>

    <b-collapse class="app-header-menu" id="nav-collapse" is-nav>
      <b-navbar-nav v-show="!isOffline" class="ml-auto">
        <!-- Customers without an order yet shall find the pricing without searching the settings menu.
             First in the menu, as the menu is right-aligned: appearing later, it moves no other entry. -->
        <b-nav-item v-if="showPricingInNavbar" class="link" href="/settings/pricing" :active="isActiveRoute(['/settings/pricing'])">
          <span class="nav-item-text">{{ $t("pricing") }}</span>
        </b-nav-item>

        <b-nav-item v-if="hasPerm(p.READ_DASHBOARD)" class="link" href="/dashboard" :active="isActiveRoute(['/dashboard'])">
          <span class="nav-item-text">{{ $t("dashboard") }}</span>
        </b-nav-item>

        <b-nav-item v-if="hasPermPlantView" class="link" href="/plants" :active="isActiveRoute(['/plants', '/plant/*'])">
          <span class="nav-item-text">{{ $t("plants") }}</span>
        </b-nav-item>

        <b-nav-item v-if="hasPerm(p.READ_ANALYSIS)" class="link" href="/analyses" :active="isActiveRoute(['/analyses', '/analysis/new'])">
          <span class="nav-item-text">{{ $t("analyses") }}</span>
        </b-nav-item>

        <b-nav-item class="link app-header-documentation-link" href="https://volateq.atlassian.net/wiki/spaces/VKB/overview" target="_blank" rel="noopener noreferrer">
          <span class="nav-item-text">
            {{ $t("documentation") }}
            <small class="pad-left-half" style="font-size: 0.6em; display: inline-block; vertical-align: top;"><app-icon icon="box-arrow-up-right" /></small>
          </span>
        </b-nav-item>
      
        <li class="nav-item-divider"></li>

        <app-notification-bell v-if="isAdmin && !isMobile" />

        <b-nav-item-dropdown v-if="isUpdateAvailable" toggle-class="app-header-nav-update-dropdown" right ref="updateDropdown">
          <template #button-content>
            <b-icon icon="cloud-upload-fill" font-scale="1.5" />
          </template>

         <b-container class="app-header-nav-update-container">
            <span class="text-default">{{ $t("new-version-available") }}</span>
            <b-button @click="onUpdateNow()">{{ $t("update-now") }}<span v-if="updateRemainingSeconds !== null"> ({{ updateRemainingSeconds }})</span></b-button>
         </b-container>
        </b-nav-item-dropdown>
      
        <b-nav-item-dropdown toggle-class="app-header-dropdown" class="app-header-dropdown-list" right>
          <template #button-content>
            <small>
              <app-icon :icon="selectedThemeIcon" scale="0.9"/>
            </small>
          </template>

          <b-dropdown-item v-for="t in themeOptions" @click="onSelectTheme(t.id)">
            <app-icon :icon="themeIcons[t.id]" /><span class="pad-left-half">{{ t.label }}</span>
          </b-dropdown-item>
        </b-nav-item-dropdown>

        <b-nav-item-dropdown toggle-class="app-header-dropdown" class="app-header-dropdown-list" right>
          <template #button-content>
            <small>
              <app-icon icon="globe" /> <span class="">{{ currentLocale.toUpperCase() }}</span>
            </small>
          </template>

          <b-dropdown-item v-for="l in locales" @click="onLanguageClick(l)">{{ $t(l) }}</b-dropdown-item>
        </b-nav-item-dropdown>

        <b-nav-item-dropdown v-if="walletCredits"
          :toggle-class="'app-header-credits' + (walletCreditsExceeded ? ' exceeded' : '')"
          :title="$t('wallet-credits_expl')"
          right
          @show="onShowWalletOrdersInfo"
        >
          <template #button-content>
            <small><app-icon icon="wallet2" /><span class="pad-left-half">{{ walletCreditsLabel }}</span></small>
          </template>

          <b-dropdown-text tag="div" class="app-header-credits-info">
            <div v-if="walletOrdersInfoLoading" class="text-center"><b-spinner small /></div>
            <template v-else-if="walletOrdersInfo">
              <div v-for="(order, index) in walletOrdersInfo.orders" :key="index" class="app-header-credits-info-order">
                <div class="app-header-credits-info-label">{{ $t("credits-valid-until") }}</div>
                <div class="app-header-credits-info-value">{{ formatDate(order.valid_until) }}</div>
                <div class="app-header-credits-info-label">{{ $t("packages") }}</div>
                <app-order-pps-view :plainProductPackages="order.product_packages" />
                <div class="app-header-credits-info-label">{{ $t("credits-booked-annual-plan") }}</div>
                <div class="app-header-credits-info-value">{{ annualPlanLabel(order.booked_credits) }}</div>
              </div>
              <template v-if="walletOrdersInfo.f2c_email">
                <div class="app-header-credits-info-label">{{ $t("credits-account-manager") }}</div>
                <a class="app-header-credits-info-value" :href="'mailto:' + walletOrdersInfo.f2c_email">{{ walletOrdersInfo.f2c_email }}</a>
              </template>
            </template>
          </b-dropdown-text>
        </b-nav-item-dropdown>

        <b-nav-item-dropdown v-if="isAdmin" toggle-class="app-header-nav-dropdown app-header-admin-dropdown" right>
          <template #button-content>
            <b-icon icon="shield-lock-fill" font-scale="1.5" :title="$t('admin')" />
          </template>

          <b-dropdown-header>{{ $t("admin-menu-business") }}</b-dropdown-header>
          <b-dropdown-item href="/settings/customers" v-if="hasPerm(p.READ_CUSTOMER)" class="link">
            {{ $t("customers") }} <app-icon icon="building" />
          </b-dropdown-item>
          <b-dropdown-item href="/settings/product-packages" v-if="hasPerm(p.READ_PRODUCT_PACKAGE)" class="link">
            {{ $t("product-packages") }} <app-icon icon="box-seam" />
          </b-dropdown-item>
          <b-dropdown-item href="/settings/orders" v-if="hasPerm(p.READ_ORDER)" class="link">
            {{ $t("orders") }} <app-icon icon="receipt" />
          </b-dropdown-item>
          <b-dropdown-item href="/settings/core-metadata" v-if="hasPerm(p.UPDATE_CORE_METADATA)" class="link">
            {{ $t("core-metadata") }} <app-icon icon="layers" />
          </b-dropdown-item>

          <b-dropdown-header>{{ $t("admin-menu-operations") }}</b-dropdown-header>
          <b-dropdown-item href="/settings/drones" v-if="hasPerm(p.READ_DRONE)" class="link">
            {{ $t("drones") }} <app-icon icon="joystick" />
          </b-dropdown-item>
          <b-dropdown-item href="/settings/monitoring" v-if="hasPerm(p.READ_ADMIN_TASK, p.READ_QFLY_SERVER)" class="link">
            {{ $t("monitoring") }} <app-icon icon="activity" />
          </b-dropdown-item>
          <b-dropdown-item href="/settings/volafly" v-if="hasPerm(p.READ_VOLAFLY_LOG)" class="link">
            {{ $t("volafly") }} <app-icon icon="journal-code" />
          </b-dropdown-item>

          <b-dropdown-header>{{ $t("admin-menu-ml") }}</b-dropdown-header>
          <b-dropdown-item href="/settings/ml-training" v-if="hasPerm(p.READ_ML_TRAINING)" class="link">
            {{ $t("ml-training") }} <app-icon icon="cpu" />
          </b-dropdown-item>
          <b-dropdown-item href="/settings/pv-training-export" v-if="hasPerm(p.READ_ML_TRAINING)" class="link">
            {{ $t("pv-training-export") }} <app-icon icon="box-arrow-up" />
          </b-dropdown-item>
          <b-dropdown-item href="/settings/ml-models" v-if="hasPerm(p.READ_ML_TRAINING)" class="link">
            {{ $t("ml-models") }} <app-icon icon="diagram-3" />
          </b-dropdown-item>

          <template v-if="hasPerm(p.SWITCH_CUSTOMER)">
            <b-dropdown-divider />
            <b-dropdown-form>
              <div class=""><small class="text-muted" v-if="!!selectedCustomer">{{ selectedCustomer.name }}</small></div>
              <app-button variant="secondary" cls="width-100pc" @click="onShowSwitchCustomerModal" :title="$t('switch-customer')">
                {{ $t("switch-customer") }} <app-icon icon="arrow-left-right" />
              </app-button>
            </b-dropdown-form>
          </template>
        </b-nav-item-dropdown>

        <b-nav-item-dropdown toggle-class="app-header-nav-dropdown" right>
          <template #button-content>
            <!-- Only for christmas (xmas) time -->
            <!-- <img src="/images/x-mas-head.png" class="xmas-head" v-if="!isMobile" /> -->
            <b-icon icon="gear-fill" font-scale="1.5" />
          </template>

          <b-dropdown-item href="/settings/users" v-if="hasPerm(p.READ_USER)" class="link">
            {{ $t("users") }} <app-icon icon="people-fill" />
          </b-dropdown-item>
          <b-dropdown-item href="/settings/pricing" v-if="showPricing && !showPricingInNavbar" class="link">
            {{ $t("pricing") }} <app-icon icon="tag-fill" /><app-super-admin-marker v-if="pricingAdminOnly" />
          </b-dropdown-item>

          <b-dropdown-divider v-if="hasPerm(p.READ_USER) || (showPricing && !showPricingInNavbar)" />

          <!-- A hidden super admin has no admin menu: switching here is the only way to unhide again. -->
          <template v-if="isHiddenSuperAdmin && hasPerm(p.SWITCH_CUSTOMER)">
            <b-dropdown-form>
              <div class=""><small class="text-muted" v-if="!!selectedCustomer">{{ selectedCustomer.name }}</small></div>
              <app-button variant="secondary" cls="width-100pc" @click="onShowSwitchCustomerModal" :title="$t('switch-customer')"> {{ $t("switch") }}<app-super-admin-marker /></app-button>
            </b-dropdown-form>
            <b-dropdown-divider />
          </template>

          <b-dropdown-item href="/settings/user-profile" class="link">
            {{ $t("user-profile") }} <app-icon icon="person-fill" />
          </b-dropdown-item>

          <b-dropdown-item v-if="hasPerm(p.MANAGE_API_KEY)" href="/doc/public-api" class="link">
            {{ $t("public-api") }} <app-icon icon="code-slash" />
          </b-dropdown-item>

          <b-dropdown-item v-if="hasPerm(p.READ_RELEASE_NOTES)" class="link" @click="onShowReleaseNotes">
            {{ $t("info") }} <b-icon icon="info-circle-fill" scale="0.9" />
          </b-dropdown-item>

          <b-dropdown-divider />

          <b-dropdown-form>
            <b-button @click="onLogout" class="width-100pc">{{ $t("logout") }}</b-button>
          </b-dropdown-form>
        </b-nav-item-dropdown>
      </b-navbar-nav>
    </b-collapse>

    <app-modal-form v-if="hasPerm(p.SWITCH_CUSTOMER)"
      id="switch-customer-modal"
      ref="switchCustomerModal"
      :title="$t('switch')"
      :subtitle="$t('switch-customer-and-change-PIs-view')"
      :superAdminProtected="true"
      :modalLoading="switchCustomerLoading"
      :okTitle="$t('apply')"
      :cancelTitle="$t('cancel')"
      @cancel="onSwitchCustomerModalCancel"
      @submit="onCustomerSwitchSubmit"
    >
      <app-alert variant="info" v-show="!selectedCustomerId"><span v-html="$t('info-no-customer-selected')"></span></app-alert>
      <b-form-group :label="$t('select-target-customer')">
        <app-select
          v-model="selectedCustomerId"
          :options="customerSelection"
          @open="onOpenCustomerSelection"
          :loading="customerSelectionLoading"
          :searchable="true"
        />
      </b-form-group>
      <b-form-group :label="$t('change-pi-view-availability')">
        <b-form-checkbox v-model="showAllKeyFigures">
          {{ $t('show-all-pis') }}
        </b-form-checkbox>
      </b-form-group>
      <b-form-group :label="$t('hide-super-admin')">
        <b-form-checkbox v-model="hideSuperAdmin">
          {{ $t('hide-super-admin') }}
        </b-form-checkbox>
      </b-form-group>
    </app-modal-form>
    <app-release-notes :showReleaseNotes="showReleaseNotes" v-if="!isOffline" />
    <app-notes-modal v-if="isAdmin" />
  </b-navbar>
</template>

<script lang="ts">
import { Component, Prop, Ref, Watch } from "vue-property-decorator";
import { BaseAuthComponent } from "@/app/shared/components/base-auth-component/base-auth-component";
import AppSuperAdminMarker from "@/app/shared/components/app-super-admin-marker/app-super-admin-marker.vue";
import AppButton from "@/app/shared/components/app-button/app-button.vue";
import AppModalForm from "@/app/shared/components/app-modal/app-modal-form.vue";
import AppIcon from "@/app/shared/components/app-icon/app-icon.vue";
import AppNotificationBell from "@/app/shared/components/app-notification-bell/app-notification-bell.vue";
import AppNotesModal from "@/app/shared/components/app-notes-modal/app-notes-modal.vue";
import { IAppModalForm } from "../app-modal/types";
import { sortAlphabetical } from "../../services/helper/sort-helper";
import { environment } from "@/environment/environment";
import { CatchError } from '@/app/shared/services/helper/catch-helper';
import { volateqApi } from "../../services/volateq-api/volateq-api";
import AppReleaseNotes from "@/app/release-notes/release-notes.vue";
import { BaseComponent } from "../base-component/base-component";
import { i18n } from "@/main";
import AppDropdownButton from "../app-dropdown-button/app-dropdown-button.vue";
import AppDropdownButtonItem from "../app-dropdown-button/app-dropdown-button-item.vue";
import { appLocalStorage } from "../../services/app-storage/app-storage";
import AppSelect from "../app-select/app-select.vue";
import { MultiselectOption } from "../app-multiselect/types";
import AppAlert from "../app-alert/app-alert.vue";
import { Theme, themeService } from "../../services/theme-service/theme-service";
import { whiteLabelingService } from "../../services/white-labeling-service/white-labeling-service";
import { deploymentService } from "../../services/deployment-service/deployment-service";
import { ReloadHelper } from "../../services/helper/reload-helper";
import { PricingHelper } from "../../services/helper/pricing-helper";
import { OptionalFeatureAccess } from "../../services/helper/optional-feature-helper";
import { OrderHelper } from "../../services/helper/order-helper";
import { userService } from "../../services/user-service/user-service";
import type { WalletCreditsSchema, WalletOrdersInfoSchema } from "../../services/volateq-api/api-schemas/customer-schemas";
import { DateHelper } from "../../services/helper/date-helper";
import AppOrderPpsView from "../app-order-pps-view/app-order-pps-view.vue";

const WALLET_CREDITS_KEY_PREFIX = "header-wallet-credits-";

/** The part of the bootstrap-vue dropdown the update announcement drives. */
interface IAppNavItemDropdown {
  show(): void;
  hide(): void;
}

@Component({
  name: "app-header",
  components: {
    AppSuperAdminMarker,
    AppButton,
    AppModalForm,
    AppIcon,
    AppNotificationBell,
    AppNotesModal,
    AppReleaseNotes,
    AppDropdownButton,
    AppDropdownButtonItem,
    AppSelect,
    AppAlert,
    AppOrderPpsView,
  }
})
export default class AppHeader extends BaseAuthComponent {
  @Prop({ default: true }) fluid!: boolean;
  @Ref() switchCustomerModal!: IAppModalForm;
  @Ref() updateDropdown!: IAppNavItemDropdown;

  themeOptions: MultiselectOption<Theme>[] = [];
  themeIcons: Record<Theme, string> = {
    default: "circle-half",
    light: "sun-fill",
    dark: "moon-fill"
  }
  selectedTheme: Theme | null = null;

  showReleaseNotes = false;

  switchCustomerLoading = false;
  customerSelection: MultiselectOption[] | null = null;
  selectedCustomerId: string | null = null;
  customerSelectionLoading = false;

  showAllKeyFigures = false;
  hideSuperAdmin = false;

  logoUrl: string | null = null;

  showPricing = false;
  // Pricing is not enabled for the customer, only a Volateq admin sees it
  pricingAdminOnly = false;
  showPricingInNavbar = false;
  walletCredits: WalletCreditsSchema | null = null;
  walletOrdersInfo: WalletOrdersInfoSchema | null = null;
  walletOrdersInfoLoading = false;

  async created() {
    this.hideSuperAdmin = this.isHiddenSuperAdmin;

    this.setLogoImg();
    this.autoSendDiagnostics();

    this.selectedTheme = themeService.getSelectedTheme();

    this.showUpdateDropdown();
    this.loadPricing();

    this.themeOptions = [
      { id: "default", label: this.$t("browser-default").toString() },
      { id: "light", label: this.$t("light").toString() },
      { id: "dark", label: this.$t("dark").toString() },
    ];
  }

  get currentPlantName(): string | null {
    return this.store.plant.currentPlant?.name || null;
  }

  get isPlantUnderConstruction(): boolean {
    return this.store.plant.currentPlant?.under_construction || false;
  }

  get walletCreditsLabel(): string {
    if (!this.walletCredits) {
      return "";
    }
    const leftCredits = this.walletCredits.booked_credits - this.walletCredits.used_credits;
    return this.$t("credits-left", { credits: OrderHelper.formatCreditAmount(leftCredits) }).toString();
  }

  get walletCreditsExceeded(): boolean {
    return !!this.walletCredits && this.walletCredits.used_credits > this.walletCredits.booked_credits;
  }

  /** Neither the pricing menu entry nor the credit count are essential, so a failed lookup only hides them. */
  private async loadPricing() {
    if (this.isOffline) {
      return;
    }

    // The credits are only known after the user lookup, so they are hidden by default. Once shown for a user
    // (and customer, as admins switch), the next page load shows the last known ones right away instead.
    const rememberedCredits = this.store.auth.userId
      ? appLocalStorage.getItem<WalletCreditsSchema>(this.walletCreditsKey(this.store.auth.userId))
      : null;
    this.walletCredits = rememberedCredits;
    try {
      const user = await userService.me();
      this.store.auth.updateUserId(user.id);
      const isVolateqAdmin = this.store.auth.isVolateqAdmin;
      const pricingAccess = PricingHelper.getPricingAccess(user, this.isAdmin, isVolateqAdmin);
      this.showPricing = pricingAccess !== OptionalFeatureAccess.HIDDEN;
      this.pricingAdminOnly = pricingAccess === OptionalFeatureAccess.ADMIN_ONLY;
      await Promise.all([
        this.loadWalletCredits(user.id, PricingHelper.canSeeWalletCredits(user, isVolateqAdmin)),
        this.showPricing && !isVolateqAdmin && this.loadPricingInNavbar(),
      ]);
    } catch (e) {
      console.error(e);
    }
  }

  private walletCreditsKey(userId: string): string {
    return `${WALLET_CREDITS_KEY_PREFIX}${userId}-${this.store.auth.customer?.id ?? ""}`;
  }

  private async loadWalletCredits(userId: string, canSeeWalletCredits: boolean) {
    this.walletCredits = canSeeWalletCredits ? await volateqApi.getOwnCustomerWalletCredits() : null;

    if (this.walletCredits) {
      appLocalStorage.setItem(this.walletCreditsKey(userId), this.walletCredits);
    } else {
      appLocalStorage.removeItem(this.walletCreditsKey(userId));
    }
  }

  private async loadPricingInNavbar() {
    this.showPricingInNavbar = !(await volateqApi.getOwnCustomerHasOrders());
  }

  @CatchError("walletOrdersInfoLoading")
  async onShowWalletOrdersInfo() {
    if (this.walletOrdersInfo) {
      return;
    }

    this.walletOrdersInfo = await volateqApi.getOwnCustomerWalletOrdersInfo();
  }

  formatDate(isoDate: string): string {
    return DateHelper.toDate(isoDate);
  }

  annualPlanLabel(bookedCredits: number): string {
    return PricingHelper.annualPlanLabel(bookedCredits, this.$t("credits").toString());
  }

  private autoSendDiagnostics() {
    if (!BaseComponent.hasDiagnosticEventAdded) {
      document.addEventListener("visibilitychange", async () => {
        if (document.hidden) {
          await this.sendDiagnostics();
        }
      });
      window.addEventListener("beforeunload", async () => {
        await this.sendDiagnostics();
      });

      BaseComponent.hasDiagnosticEventAdded = true;
    }
  }

  private async setLogoImg() {
    this.logoUrl = await whiteLabelingService.getLogoUrl();
  }

  private async sendDiagnostics() {
    if (this.store.auth.isAuthenticated && BaseComponent.diagnosticErrors.length > 0 && !this.isOffline) {
      await volateqApi.sendDiagnostics(BaseComponent.diagnosticErrors);

      BaseComponent.diagnosticErrors = [];
    }
  }

  get isUpdateAvailable(): boolean {
    return this.store.deployment.isUpdateAvailable;
  }

  get updateRemainingSeconds(): number | null {
    return this.store.deployment.remainingSeconds;
  }

  // The deployment service only announces an update if it could not apply it unnoticed,
  // so whenever this becomes true the dropdown has to be opened.
  @Watch("isUpdateAvailable")
  onUpdateAvailableChanged() {
    this.showUpdateDropdown();
  }

  @CatchError()
  onUpdateNow(): void {
    deploymentService.reloadNow();
  }

  private async showUpdateDropdown(): Promise<void> {
    if (!this.isUpdateAvailable) {
      return;
    }

    await this.$nextTick(); // wait for dom

    if (!this.updateDropdown) {
      return;
    }

    this.updateDropdown.show();

    // Only while the tab reloads itself anyway there is nothing to decide, so the
    // dropdown stays open. Without a countdown the update is merely offered and the
    // user has to be able to close it again.
    if (this.updateRemainingSeconds !== null) {
      this.updateDropdown.hide = () => {}; // Disallow hiding
    }
  }

  @CatchError()
  async onShowReleaseNotes() {
    this.showReleaseNotes = true;
    await this.$nextTick(); // wait for dom
    this.showReleaseNotes = false; // let user show release notes again after closing them
  }

  @CatchError()
  async onLogout(): Promise<void> {
    if ((await volateqApi.hasOfflineData()) &&
      !confirm(this.$t("logout-offline-data-lost-sure").toString())
    ) {
      return;
    }

    await volateqApi.logout();

    this.$router.push({ name: "Login" });
    this.$router.go(0); // Reload the page to refresh the js cache
  }

  isActiveRoute(routes: string[]): boolean {
    return !!routes.find(route =>
      route.indexOf("*") != -1
        ? this.$router.currentRoute.path.replace(route.replace("*", ""), "").indexOf("/") == -1
        : this.$router.currentRoute.path === route
    );
  }

  get isEnvDevelopment(): boolean {
    return environment === "development";
  }

  get isEnvTest(): boolean {
    return environment === "staging";
  }
  
  get isEnvProduction(): boolean {
    return environment === "production";
  }

  get isMobile(): boolean {
    return this.store.mobile.isMobile;
  }

  public get isOffline(): boolean {
    return this.store.offline.isOffline;
  }

  get currentLocale(): string {
    return i18n.locale;
  }

  get locales(): string[] {
    return Object.keys(i18n.messages);
  }

  @CatchError()
  onLanguageClick(locale: string) {
    appLocalStorage.setItem("locale", locale)
    
    ReloadHelper.forceReload();
  }

  @CatchError("customerSelectionLoading")
  async onOpenCustomerSelection() {
    if (this.customerSelection === null || this.customerSelection.length === 1) {
      this.customerSelection = sortAlphabetical(await volateqApi.getCustomers(), "name").map(c => ({
        id: c.id,
        label: c.name,
      }));
    }
  }
  
  @CatchError()
  async onShowSwitchCustomerModal() {
    if (this.customerSelection === null && this.selectedCustomer) {
      this.customerSelection = [{ id: this.selectedCustomer.id, label: this.selectedCustomer.name }];
    }
    this.selectedCustomerId = this.selectedCustomer?.id || null;

    const user = await volateqApi.getMe();
    this.showAllKeyFigures = user.profile?.show_all_key_figures || false;
    this.hideSuperAdmin = user.profile?.hide_admin_features || false;

    this.switchCustomerModal.show();
  }

  @CatchError()
  async onSwitchCustomerModalCancel() {
    this.switchCustomerModal.hide();
  }

  @CatchError("switchCustomerLoading")
  async onCustomerSwitchSubmit() {
    const tokenResult = await volateqApi.switchCustomer(
      this.selectedCustomerId || undefined,
      this.showAllKeyFigures,
      this.hideSuperAdmin,
    );

    const selectedCustomer = this.customerSelection?.find(customer => customer.id === this.selectedCustomerId);

    this.store.auth.updateCustomer(selectedCustomer && { id: selectedCustomer.id, name: selectedCustomer.label });
    this.store.auth.updateHidden(this.hideSuperAdmin);
    this.store.auth.updateToken({
      token: tokenResult.token,
      permissions: tokenResult.permissions,
      customer: tokenResult.customer,
      hiddenAdmin: this.hideSuperAdmin,
      isAdmin: tokenResult.is_volateq_admin,
      refreshToken: tokenResult.refresh_token,
      whiteLabelingVersion: tokenResult.white_labeling_version
    });

    this.$router.go(0);
  }

  @CatchError()
  async onSelectTheme(theme: Theme) {
    themeService.setSelectedTheme(theme);
    themeService.updateTheme();

    this.selectedTheme = theme;
  }

  get selectedThemeIcon(): string {
    return this.selectedTheme ? this.themeIcons[this.selectedTheme] : "";
  }

}
</script>

<style lang="scss">
@import "@/scss/_colors.scss";
@import "@/scss/_variables.scss";
@import "~bootstrap/scss/_functions";
@import "~bootstrap/scss/_variables";
@import "~bootstrap/scss/mixins/_breakpoints";

%padtopbot-0 {
  padding-top: 0;
  padding-bottom: 0;
}

@keyframes animatedBackground {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 0px var(--header-height);
  }
}

.app-release-notes {
  overflow-y: auto;
}

.app-header {
  position: relative;
  height: var(--header-height);
  line-height: var(--header-height);
  background-color: var(--muted-bg-color);
  color: var(--secondary-text-color);
  border-bottom: 1px solid var(--default-border-color);

  @extend %padtopbot-0;

  &-dev-badge {
    position: absolute;
    top: 8px;
    left: 120px;
  }

  .navbar-toggler {
    border-radius: 0;
    border: none;
    padding-left: 0;
    padding-right: 0;

    &:hover, &:active, &:focus {
      border: none;
      outline: none;
    }
  }

  .xmas-head {
    position: absolute;
    width: 35px;
    top: 15px;
    right: -5px;
  }

  @include media-breakpoint-down(sm) {
    .navbar-collapse {
      margin-right: -1rem;
      margin-left: -1rem;
    }
  }

  &-menu {
    box-sizing: border-box;

    .dropdown-item {
      color: var(--primary-bg-color);

      &:hover {
        background-color: var(--secondary-bg-highlight-color); 
      }

      &:focus,
      &:focus-visible,
      &:active {
        color: var(--primary-bg-color);
        background-color: var(--secondary-bg-highlight-color);
        outline: none;
        box-shadow: none;
      }
    }

    .nav-link {
      border-bottom: 1px solid transparent;

      @media (max-width: 991px) {
        background-color: var(--muted-bg-color);
      }

      &:focus {
        color: var(--muted-text-color) !important;
      }

      &:not(.dropdown .nav-link) {
        $transition: 0.2s ease-in-out;
        padding: 0 30px !important;
        color: var(--secondary-text-color);
        transition: background $transition, color $transition;

        &:hover {
          background-color: var(--secondary-bg-highlight-color);
          color: var(--secondary-btn-text-color);
          border-bottom-color: var(--primary-bg-color);
        }

        &.active {
          background-color: var(--primary-bg-color);
          color: var(--primary-text-color);

          &:hover {
            background-color: var(--secondary-bg-highlight-color);
            color: var(--secondary-btn-text-color);
          }
        }
      }
    }
  }

  &-documentation-link {
    white-space: nowrap;
  }

  .navbar-brand {
    height: var(--header-height);
    @extend %padtopbot-0;
  }

  &-logo {
    height: var(--header-height);
    margin-left: -15px;
    @extend %padtopbot-0;
  }

  // Same look as the theme and language toggles; .navbar-nav .nav-link needs the extra specificity.
  .navbar-nav .nav-link.app-header-credits {
    margin-right: 1em;
    color: var(--muted-text-color);
    white-space: nowrap;
    &:hover {
      color: var(--secondary-text-color);
    }

    // Not --danger-text-color: in dark mode that is a near-white meant for alert backgrounds
    &.exceeded {
      color: var(--danger-outline-btn-text-color);
    }
  }

  .app-header-credits-info {
    .b-dropdown-text {
      min-width: 16em;
      padding: 0.75rem 1.5rem;
      color: var(--secondary-text-color);
    }

    &-order + &-order {
      margin-top: 0.75rem;
      padding-top: 0.75rem;
      border-top: 1px solid var(--default-border-color);
    }

    &-label {
      margin-top: 0.5rem;
      font-size: 0.8em;
      color: var(--muted-text-color);

      &:first-child {
        margin-top: 0;
      }
    }

    &-order + &-label {
      margin-top: 0.75rem;
    }

    // Nested for specificity over the link and dropdown colors
    .app-header-credits-info-value {
      display: block;
      font-weight: bold;
      color: var(--default-text-color);
    }
  }

  .dropdown {
    &.show > .app-header-nav-dropdown {
      background-color: var(--muted-bg-color);

      .bi-gear-fill {
        color: var(--secondary-text-color);
        transform: rotate(225deg);
      }

      .bi-shield-lock-fill {
        color: var(--secondary-text-color);
      }
    }

    .app-header-dropdown {
      margin-right: 1em;
      color: var(--muted-text-color);

      &:hover {
        color: var(--secondary-text-color);
      }
    }

    .app-header-admin-dropdown {
      margin-right: 1em;
    }

    .app-header-dropdown-list {
      .dropdown-menu {
        margin-right: 1em;
      }
    }

    &.show > .app-header-dropdown {
      color: var(--secondary-text-color);
    }

    &-menu {
      border-radius: 0;
      border-color: var(--default-border-color);
      background-color: var(--muted-bg-color);
      line-height: 20px;
      margin-top: -1px;
      
      .dropdown-item {
        text-align: right;
        padding: 0.75rem 1.5rem;
      }

      .b-dropdown-form {
        padding: 0.75rem 1.5rem;
      }

      // Group headings of the admin menu
      .dropdown-header {
        text-align: right;
        padding: 0.75rem 1.5rem 0.25rem;
        font-size: 0.75rem;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--muted-text-color);
      }

      .dropdown-divider {
        border-color: var(--default-border-color);
      }
    }

    &-toggle {
      height: var(--header-height);

      display: flex;
      justify-content: center;
      align-items: center;
      transition: all 0.15s linear;

      &::after {
        content: none;
      }
    }
  }

  .bi-gear-fill, .bi-shield-lock-fill, .bi-cloud-upload-fill {
    color: var(--muted-text-color);
    transform-origin: center;
    transition: all 0.3s linear;
  }

  &-nav-update-container {
    display: grid !important;
    gap: 0.75rem;
    grid-template-columns: 100%;

    span {
      font-size: 1rem;
      line-height: 1.5rem;
      text-align: center;
    }

    button {
      min-width: 20ch;
      width: 100%;
    }
  }

  @media (max-width: 991px) {
    .nav-item-divider {
      display: none;
    }
  }

  .nav-item-divider {
    width: 20px;
    height: calc(var(--header-height) - 20px);
    border-left: 1px solid var(--default-border-color);
    margin-top: 10px;
  }

  &-notification-mobile {
    margin-left: auto;
    flex-direction: row;
    align-items: center;

    .app-notification-bell-toggle {
      margin-right: 0.5rem;
      padding: 0.25rem 0.5rem;
    }

    .app-notification-bell-menu {
      margin-right: 0;
      position: absolute;
      right: 0;
      top: 100%;
    }
  }

  &-current-plant-name {
    max-width: 50%;
    margin-left: 10px;
    overflow: hidden;
    max-height: var(--header-height);
    align-self: center;
    font-size: min(1.5rem, calc((var(--header-height) - 12px) / 1.25));

    @media (max-width: 1300px) {
      font-size: min(
        clamp(0.75rem, calc(2.4vw - 0.45rem), 1.5rem),
        calc((var(--header-height) - 12px) / 1.25)
      );
    }

    .badge {
      display: block;
      max-width: 100%;
      max-height: calc(var(--header-height) - 8px);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: inherit;
      line-height: 1.2;
      padding-top: 0.1em;
      padding-bottom: 0.1em;
      box-sizing: border-box;
    }
  }

  &-current-plant-construcion-icon > svg {
    vertical-align: top;
  }
}

@media (max-width: 991px) {
.app-header {
  .dropdown {
    .app-header-locales-dropdown {
      margin-right: 0;
    }
    .app-header-dropdown,
    .app-header-admin-dropdown {
      margin-right: 0;
    }
  }
  .navbar-nav .nav-link.app-header-credits {
    margin-right: 0;
  }
}
}
</style>
